import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Plus, Trash2, Settings, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { PageHeader } from '@/components/ui/page-header'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { AutomationList } from '@/components/actions/AutomationList'
import { AutomationStats } from '@/components/actions/AutomationStats'
import { BetterplaceCrucesForm } from '@/components/actions/BetterplaceCrucesForm'
import { DeleteConfirmationDialog } from '@/components/actions/DeleteConfirmationDialog'
import { TemplateVariablesHelpDialog } from '@/components/actions/TemplateVariablesHelpDialog'
import { FacebookLeadsTemplateHelpDialog } from '@/components/settings/FacebookLeadsTemplateHelpDialog'
import { InstantFormsList } from '@/components/settings/InstantFormsList'
import { PortalLeadSettingsCard } from '@/components/settings/PortalLeadSettingsCard'
import { PostCallTemplatesSettingsCard } from '@/components/settings/PostCallTemplatesSettingsCard'
import { WebPortalNotificationsCard } from '@/components/settings/WebPortalNotificationsCard'
import { RisingOffersNotificationsCard } from '@/components/settings/RisingOffersNotificationsCard'
import { BuyerReactivationNotificationsCard } from '@/components/settings/BuyerReactivationNotificationsCard'
import { AuthOtpNotificationsCard } from '@/components/settings/AuthOtpNotificationsCard'
import { TestBenchCard } from '@/components/settings/TestBenchCard'
import { ExcludedAgentsCard } from '@/components/settings/ExcludedAgentsCard'
import { AgentNoticeSettingsCard } from '@/components/settings/AgentNoticeSettingsCard'
import { ConsentModesCard } from '@/components/settings/ConsentModesCard'
import { AutomationTemplateMetaAlerts } from '@/components/settings/AutomationTemplateMetaAlerts'
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi'
import { Switch } from '@/components/ui/switch'
import { betterplaceService } from '@/services/betterplaceService'
import type { CrossCriteria, CrossSettings } from '@/services/betterplaceService'
import { meetingAutomationService } from '@/services/meetingAutomationService'
import type { MeetingAutomationSettings } from '@/services/meetingAutomationService'
import {
  analyzeMeetingTemplate,
  buildDefaultMeetingVariableMapping,
  buildMeetingAutomationPayload,
  buildMeetingTemplateOptionNames,
  CANONICAL_MEETING_VARIABLE_SPECS,
  LEGACY_MEETING_TEMPLATE_NAMES,
} from '@/pages/meetingAutomationTemplateUtils'
import type { MeetingVariableMappingEntry } from '@/pages/meetingAutomationTemplateUtils'
import { buildNormalizedBetterplaceCrossSettings } from '@/components/actions/betterplaceTemplateUtils'
import { whatsappService, WhatsAppServiceError } from '@/services/whatsappService'
import type { WhatsAppTemplate } from '@/services/whatsappService'
import { resolvePreferredWhatsAppWabaId } from '@/services/whatsappWabaUtils'
import { ComercialPhoneBindingsCard } from '@/components/actions/ComercialPhoneBindingsCard'
import { visitSchedulingService } from '@/services/visitSchedulingService'
import type { AgencyClosureDay, AgentCalendarBinding, UpsertAgentCalendarBindingPayload, VisitDurationConfig } from '@/services/visitSchedulingService'
import type { Automation, CreateAutomationRequest, UpdateAutomationRequest } from '@/types/automation'
import { AutomationStatus } from '@/types/automation'
import { META_INTEGRATION_ENABLED, VISIT_SCHEDULING_ENABLED } from '@/config/env'
import { useLocaleSettings } from '@/hooks/use-settings'
import { useWhatsAppStatusQuery } from '@/queries'
import { toast } from 'sonner'

type MeetingTimeDirection = 'Before' | 'After'
type MeetingTimeId = 'pre_visit' | 'post_visit'

type MeetingTime = {
  id: MeetingTimeId
  name: string
  enabled: boolean
  minutes: number
  direction: MeetingTimeDirection
  linkedTemplate: string
  templateLanguage: string
  bodyVariableCount: number
  variableMapping: MeetingVariableMappingEntry[]
}

const initialMeetingTimes: MeetingTime[] = [
  {
    id: 'pre_visit',
    name: 'Previsita',
    enabled: true,
    minutes: 30,
    direction: 'Before',
    linkedTemplate: 'pre_visit',
    templateLanguage: 'es_ES',
    bodyVariableCount: 0,
    variableMapping: [],
  },
  {
    id: 'post_visit',
    name: 'Postvisita',
    enabled: true,
    minutes: 120,
    direction: 'After',
    linkedTemplate: 'post_visit',
    templateLanguage: 'es_ES',
    bodyVariableCount: 0,
    variableMapping: [],
  },
]

const FIXED_MEETING_TIME_OPTIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 300, label: '5 hours' },
] as const

const BETTERPLACE_DEFAULT_MAX_LISTINGS = 10

// Índice = el que usa el backend (0=lunes … 6=domingo), como Python weekday().
const WEEKDAY_LABEL_KEYS = [
  'actions.visit.weekday.mon',
  'actions.visit.weekday.tue',
  'actions.visit.weekday.wed',
  'actions.visit.weekday.thu',
  'actions.visit.weekday.fri',
  'actions.visit.weekday.sat',
  'actions.visit.weekday.sun',
] as const

const MEETING_TEMPLATE_SOURCE_BY_KEY: Record<(typeof CANONICAL_MEETING_VARIABLE_SPECS)[number]['key'], string> = {
  user_name: 'Backend -> meeting.user.get_display_name(). IA Gestión contact is resolved from idUsuario/contact_id or Telefono.',
  property_label: 'Backend -> meeting.property_address, fallback meeting.property_title. IA Gestión -> Direccion/direccion/property_address, fallback Titulo/title.',
  agent_name: 'Backend -> meeting.agent_name. IA Gestión -> agent_name/AgentName or scheduled-visits comercial.nombre.',
  date: 'Backend -> meeting.start_time formatted as dd/mm/yyyy. IA Gestión -> scheduled_datetime/ScheduledDateTime or FechaPlanificada + HoraPlanificada.',
  time: 'Backend -> meeting.start_time formatted as HH:mm. IA Gestión -> scheduled_datetime/ScheduledDateTime or FechaPlanificada + HoraPlanificada.',
}

const BETTERPLACE_TEMPLATE_HELP_VARIABLES = [
  {
    slot: '{{1}}',
    label: 'Buyer name',
    source: 'Betterplace -> interestedClients[].name/fullName/firstName matched by buyer phone.',
    example: 'Ana Compradora',
  },
  {
    slot: '{{2}}',
    label: 'Buyer phone',
    source: 'Betterplace -> interestedClients phone/phoneNumber/mobilePhone. Also used as the buyer recipient.',
    example: '+34600111222',
  },
  {
    slot: '{{3}}',
    label: 'Property',
    source: 'Betterplace property -> title, fallback ref/externalId/property_id. Coordination lookup can also use name/address/fullAddress.',
    example: 'Atico Centro',
  },
  {
    slot: '{{4}}',
    label: 'Property URL',
    source: 'Betterplace property -> property_url/url.',
    example: 'https://example.com/property/2064440',
  },
  {
    slot: '{{5}}',
    label: 'Property price',
    source: 'Betterplace property -> sale_price, fallback price.',
    example: '350000',
  },
  {
    slot: '{{6}}',
    label: 'Property reference',
    source: 'Betterplace property -> ref, fallback externalId/property_id.',
    example: '2064440',
  },
  {
    slot: '{{7}}',
    label: 'Cross ID',
    source: 'Betterplace cross -> crossId. Coordination fallback -> property_id:buyer_phone scope.',
    example: 'cross-123',
  },
]

const mapSettingsToMeetingTimes = (settings: MeetingAutomationSettings): MeetingTime[] => [
  {
    id: 'pre_visit',
    name: 'Previsita',
    enabled: settings.pre_visit.enabled,
    minutes: settings.pre_visit.offset_minutes,
    direction: 'Before',
    linkedTemplate: settings.pre_visit.template_name,
    templateLanguage: settings.pre_visit.template_language,
    bodyVariableCount: settings.pre_visit.body_variable_count,
    variableMapping: settings.pre_visit.variable_mapping ?? [],
  },
  {
    id: 'post_visit',
    name: 'Postvisita',
    enabled: settings.post_visit.enabled,
    minutes: settings.post_visit.offset_minutes,
    direction: 'After',
    linkedTemplate: settings.post_visit.template_name,
    templateLanguage: settings.post_visit.template_language,
    bodyVariableCount: settings.post_visit.body_variable_count,
    variableMapping: settings.post_visit.variable_mapping ?? [],
  },
]

// Mock service implementation
class MockAutomationService {
  private automations: Automation[] = []

  async getAllAutomations(): Promise<Automation[]> {
    await new Promise(resolve => setTimeout(resolve, 500))
    return [...this.automations]
  }

  async createAutomation(request: CreateAutomationRequest): Promise<Automation> {
    await new Promise(resolve => setTimeout(resolve, 800))
    
    const newAutomation: Automation = {
      id: Date.now().toString(),
      ...request,
      status: AutomationStatus.ACTIVE,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    
    this.automations.push(newAutomation)
    return newAutomation
  }

  async updateAutomation(request: UpdateAutomationRequest): Promise<Automation> {
    await new Promise(resolve => setTimeout(resolve, 600))
    
    const index = this.automations.findIndex(a => a.id === request.id)
    if (index === -1) {
      throw new Error('Automation not found')
    }
    
    const updatedAutomation: Automation = {
      ...this.automations[index],
      ...request,
      updatedAt: new Date()
    }
    
    this.automations[index] = updatedAutomation
    return updatedAutomation
  }

  async deleteAutomation(id: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400))
    
    const index = this.automations.findIndex(a => a.id === id)
    if (index === -1) {
      throw new Error('Automation not found')
    }
    
    this.automations.splice(index, 1)
  }

  async toggleAutomationStatus(id: string): Promise<Automation> {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const index = this.automations.findIndex(a => a.id === id)
    if (index === -1) {
      throw new Error('Automation not found')
    }
    
    this.automations[index] = {
      ...this.automations[index],
      status: this.automations[index].status === AutomationStatus.ACTIVE ? AutomationStatus.PAUSED : AutomationStatus.ACTIVE,
      updatedAt: new Date()
    }
    
    return this.automations[index]
  }

  async getAutomationStats(): Promise<{ total: number; active: number; paused: number }> {
    await new Promise(resolve => setTimeout(resolve, 200))
    // Ensure automations is an array
    const safeAutomations = Array.isArray(this.automations) ? this.automations : []
    return {
      total: safeAutomations.length,
      active: safeAutomations.filter(a => a.status === AutomationStatus.ACTIVE).length,
      paused: safeAutomations.filter(a => a.status === AutomationStatus.PAUSED).length
    }
  }
}

const automationService = new MockAutomationService()

// Las automatizaciones todavia no estan construidas: por eso nunca hubo un boton
// para crear una. Una pantalla que ofrece a medias algo que no existe confunde
// mas que ayuda, asi que ni la lista ni su recuento se pintan. Todo lo demas
// -el componente, sus manejadores, la ruta del lienzo- sigue entero: devolverlas
// es poner esto en `true`.
const AUTOMATIZACIONES_LISTAS = false

export function ActionsPage() {
  const { t } = useTranslation()
  // `useParams` devuelve string | undefined, y tres props de abajo esperan
  // string. Se normaliza aqui una vez en vez de poner `lang || 'en'` en cada
  // uso, que es lo que hacen ConversationsPage:765 y :1207. El mismo fichero ya
  // usaba ese fallback por su cuenta en :898.
  const { lang: langParam } = useParams<{ lang: string }>()
  const lang = langParam || 'en'
  const { selectedTimezone } = useLocaleSettings()
  
  const [automations, setAutomations] = useState<Automation[]>([])
  const [stats, setStats] = useState({ total: 0, active: 0, paused: 0 })
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'flows' | 'schedules'>('flows')
  // legacy flag left for compatibility; not used
  // const [showForm] = useState(false)
  // const [editingAutomation] = useState<Automation | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; automation: Automation | null }>({
    open: false,
    automation: null
  })
  // const [formLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [meetingTimes, setMeetingTimes] = useState<MeetingTime[]>(initialMeetingTimes)
  const [meetingTimesDraft, setMeetingTimesDraft] = useState<MeetingTime[]>(initialMeetingTimes)
  const [meetingTimeLoading, setMeetingTimeLoading] = useState(false)
  const [meetingTimeSaving, setMeetingTimeSaving] = useState(false)
  const [meetingTimeError, setMeetingTimeError] = useState<string | null>(null)
  const [calendarBindings, setCalendarBindings] = useState<AgentCalendarBinding[]>([])
  const [calendarBindingsLoading, setCalendarBindingsLoading] = useState(false)
  const [calendarBindingsSavingId, setCalendarBindingsSavingId] = useState<string | null>(null)
  const [calendarBindingsError, setCalendarBindingsError] = useState<string | null>(null)
  const [creatingBinding, setCreatingBinding] = useState(false)
  const [durationSaving, setDurationSaving] = useState(false)
  const [durationConfig, setDurationConfig] = useState<VisitDurationConfig>({
    samePropertyMinutes: 30,
    differentPropertyMinutes: 45,
    minNoticeHours: 24,
    minNoticeBusinessHours: true,
    workdayMorningStart: '09:00',
    workdayMorningEnd: '14:00',
    workdayAfternoonStart: '16:00',
    workdayAfternoonEnd: '19:00',
    workdays: [0, 1, 2, 3, 4],
  })
  const [closureDays, setClosureDays] = useState<AgencyClosureDay[]>([])
  const [newClosureDraft, setNewClosureDraft] = useState({ date: '', reason: '' })
  const [closureSaving, setClosureSaving] = useState(false)
  const [newBindingDraft, setNewBindingDraft] = useState<UpsertAgentCalendarBindingPayload>({
    idGestor: '',
    agentDisplayName: '',
    googleCalendarId: '',
    personalCalendarIds: [],
    timezone: selectedTimezone,
  })

  const [isCampaignLinksOpen, setIsCampaignLinksOpen] = useState(false)
  const [isCampaignTemplatesHelpOpen, setIsCampaignTemplatesHelpOpen] = useState(false)
  const [isMeetingTemplatesOpen, setIsMeetingTemplatesOpen] = useState(false)
  const [isMeetingTemplatesHelpOpen, setIsMeetingTemplatesHelpOpen] = useState(false)
  const [isMeetingTimeOpen, setIsMeetingTimeOpen] = useState(false)
  const [varMappingModal, setVarMappingModal] = useState<{ open: boolean; slotId: MeetingTimeId | null; draft: MeetingVariableMappingEntry[] }>({
    open: false,
    slotId: null,
    draft: [],
  })
  const [isAssignmentsOpen, setIsAssignmentsOpen] = useState(false)
  const [isBetterplaceOpen, setIsBetterplaceOpen] = useState(false)
  const [isBetterplaceTemplatesHelpOpen, setIsBetterplaceTemplatesHelpOpen] = useState(false)

  // Betterplace: sync config (form uses interval_hours; API uses interval_minutes)
  const [betterplaceDraft, setBetterplaceDraft] = useState({
    enabled: false,
    interval_hours: 24,
    max_listings: BETTERPLACE_DEFAULT_MAX_LISTINGS
  })
  const [betterplaceLoading, setBetterplaceLoading] = useState(false)
  const [betterplaceSaving, setBetterplaceSaving] = useState(false)
  const [betterplaceError, setBetterplaceError] = useState<string | null>(null)

  // Betterplace: toggle independiente de envíos WhatsApp de cruces (template-dispatch del portal)
  const [whatsappDispatchEnabled, setWhatsappDispatchEnabled] = useState(false)
  const [whatsappDispatchLoading, setWhatsappDispatchLoading] = useState(false)
  const [whatsappDispatchSaving, setWhatsappDispatchSaving] = useState(false)
  const [whatsappDispatchError, setWhatsappDispatchError] = useState<string | null>(null)

  const [crossCriteriaDraft, setCrossCriteriaDraft] = useState<CrossCriteria>({})
  const [crossCriteriaLoading, setCrossCriteriaLoading] = useState(false)
  const [crossCriteriaSaving, setCrossCriteriaSaving] = useState(false)
  const [crossCriteriaError, setCrossCriteriaError] = useState<string | null>(null)

  const [crossSettingsDraft, setCrossSettingsDraft] = useState<CrossSettings>({})
  const [crossSettingsLoading, setCrossSettingsLoading] = useState(false)
  const [crossSettingsSaving, setCrossSettingsSaving] = useState(false)
  const [crossSettingsError, setCrossSettingsError] = useState<string | null>(null)

  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([])
  const [betterplaceWhatsappTemplates, setBetterplaceWhatsappTemplates] = useState<WhatsAppTemplate[]>([])
  const [whatsappTemplatesLoading, setWhatsappTemplatesLoading] = useState(false)
  const [whatsappTemplatesLoadError, setWhatsappTemplatesLoadError] = useState<string | null>(null)
  const [whatsappTemplatesHadWaba, setWhatsappTemplatesHadWaba] = useState(false)
  const shouldLoadWhatsAppStatus =
    META_INTEGRATION_ENABLED && (isCampaignLinksOpen || isMeetingTemplatesOpen || isBetterplaceOpen)
  const {
    data: whatsappStatus,
    error: whatsappStatusError,
    isLoading: whatsappStatusLoading,
  } = useWhatsAppStatusQuery({ enabled: shouldLoadWhatsAppStatus })
  const whatsappConnected = Boolean(whatsappStatus?.connected)

  const [initialBetterplace, setInitialBetterplace] = useState<{ enabled: boolean; interval_hours: number; max_listings: number } | null>(null)
  const [initialCrossCriteria, setInitialCrossCriteria] = useState<CrossCriteria | null>(null)
  const [initialCrossSettings, setInitialCrossSettings] = useState<CrossSettings | null>(null)

  const hasBetterplaceChanges = initialBetterplace
    ? betterplaceDraft.enabled !== initialBetterplace.enabled ||
      betterplaceDraft.interval_hours !== initialBetterplace.interval_hours ||
      betterplaceDraft.max_listings !== initialBetterplace.max_listings
    : false
  const hasCrossCriteriaChanges =
    initialCrossCriteria !== null &&
    JSON.stringify(crossCriteriaDraft) !== JSON.stringify(initialCrossCriteria)
  const hasCrossSettingsChanges =
    initialCrossSettings !== null &&
    JSON.stringify(crossSettingsDraft) !== JSON.stringify(initialCrossSettings)
  const hasMeetingTimeChanges =
    JSON.stringify(meetingTimesDraft) !== JSON.stringify(meetingTimes)

  const updateBindingDraft = (id: string, updates: Partial<AgentCalendarBinding>) => {
    setCalendarBindings(prev =>
      prev.map((binding) => (binding.id === id ? { ...binding, ...updates } : binding))
    )
  }

  const updateMeetingTimeDraft = (id: string, updates: Partial<MeetingTime>) => {
    setMeetingTimesDraft(prev =>
      prev.map((mt) => (mt.id === id ? { ...mt, ...updates } : mt))
    )
  }

  const templateByName = new Map(whatsappTemplates.map((template) => [template.name, template]))
  const meetingTemplateVariableLabels: Record<(typeof CANONICAL_MEETING_VARIABLE_SPECS)[number]['key'], string> = {
    user_name: t('actions.meeting_templates.help.variable_client_name'),
    property_label: t('actions.meeting_templates.help.variable_property'),
    agent_name: t('actions.meeting_templates.help.variable_agent_name'),
    date: t('actions.meeting_templates.help.variable_date'),
    time: t('actions.meeting_templates.help.variable_time'),
  }
  const meetingTemplateHelpVariables = CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => ({
    slot: spec.slot,
    label: meetingTemplateVariableLabels[spec.key],
    source: MEETING_TEMPLATE_SOURCE_BY_KEY[spec.key],
    example: spec.example,
  }))
  const meetingTemplateOptions = buildMeetingTemplateOptionNames(whatsappTemplates, meetingTimesDraft)
  const hasSelectableMeetingTemplates = meetingTemplateOptions.length > 0
  const hasLegacyMeetingFallback = meetingTimesDraft.some((meetingTime) =>
    LEGACY_MEETING_TEMPLATE_NAMES.has(meetingTime.linkedTemplate)
  )
  const showEmptyTemplatesHint =
    !whatsappTemplatesLoadError &&
    !whatsappTemplatesLoading &&
    whatsappTemplatesHadWaba &&
    whatsappTemplates.length === 0
  const showLegacyFallbackHint =
    !showEmptyTemplatesHint &&
    !whatsappTemplatesLoadError &&
    !whatsappTemplatesLoading &&
    hasLegacyMeetingFallback
  const hasIncompatibleMeetingTemplates = meetingTimesDraft.some((meetingTime) => {
    const template = templateByName.get(meetingTime.linkedTemplate)
    const analysis = analyzeMeetingTemplate(template, meetingTime.linkedTemplate)
    return !analysis.compatible
  })

  const getMeetingTimeErrorMessage = (error: unknown, fallbackKey: string) => {
    const rawMessage = error instanceof Error ? error.message : ''
    if (rawMessage.includes('Missing required fields')) {
      return t('actions.visit.errors.required_fields')
    }
    return rawMessage || t(fallbackKey)
  }

  const handleSaveMeetingTimes = async () => {
    setMeetingTimeSaving(true)
    setMeetingTimeError(null)
    try {
      const payload: MeetingAutomationSettings = buildMeetingAutomationPayload(meetingTimesDraft, whatsappTemplates)
      const saved = await meetingAutomationService.updateSettings(payload)
      const nextTimes = mapSettingsToMeetingTimes(saved)
      setMeetingTimes(nextTimes)
      setMeetingTimesDraft(nextTimes)
      toast.success(t('common.save'))
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.meeting_templates.errors.save_error')
      setMeetingTimeError(message)
      toast.error(message)
    } finally {
      setMeetingTimeSaving(false)
    }
  }

  const handleCancelMeetingTimes = () => {
    setMeetingTimesDraft(meetingTimes)
  }


  useEffect(() => {
    loadAutomations()
  }, [])

  useEffect(() => {
    if (!META_INTEGRATION_ENABLED || !isMeetingTemplatesOpen) return
    if (whatsappStatusLoading) return

    const loadTemplates = async () => {
      setWhatsappTemplatesLoading(true)
      setWhatsappTemplatesLoadError(null)
      setWhatsappTemplatesHadWaba(false)

      if (whatsappStatusError) {
        setWhatsappTemplates([])
        setWhatsappTemplatesLoadError(
          whatsappStatusError instanceof Error
            ? whatsappStatusError.message
            : t('actions.meeting_templates.templates_fetch_error')
        )
        setWhatsappTemplatesLoading(false)
        return
      }

      if (whatsappStatus?.connected === false) {
        setWhatsappTemplates([])
        setWhatsappTemplatesLoadError(null)
        setWhatsappTemplatesLoading(false)
        return
      }

      try {
        const wabas = await whatsappService.getWabas()
        const wabaId = resolvePreferredWhatsAppWabaId(wabas, whatsappStatus?.account_id)
        if (!wabaId) {
          setWhatsappTemplates([])
          setWhatsappTemplatesLoadError(t('actions.meeting_templates.no_waba_hint'))
          return
        }
        setWhatsappTemplatesHadWaba(true)
        try {
          const templates = await whatsappService.getTemplates(wabaId)
          setWhatsappTemplates(templates)
          setWhatsappTemplatesLoadError(null)
        } catch (inner) {
          setWhatsappTemplates([])
          const msg =
            inner instanceof WhatsAppServiceError
              ? inner.message
              : t('actions.meeting_templates.templates_fetch_error')
          setWhatsappTemplatesLoadError(msg)
        }
      } catch (outer) {
        setWhatsappTemplates([])
        setWhatsappTemplatesHadWaba(false)
        const msg =
          outer instanceof WhatsAppServiceError
            ? outer.message
            : t('actions.meeting_templates.templates_fetch_error')
        setWhatsappTemplatesLoadError(msg)
      } finally {
        setWhatsappTemplatesLoading(false)
      }
    }

    loadTemplates()
  }, [isMeetingTemplatesOpen, t, whatsappStatus?.account_id, whatsappStatus?.connected, whatsappStatusError, whatsappStatusLoading])

  useEffect(() => {
    if (!isMeetingTemplatesOpen) return

    const loadMeetingSettings = async () => {
      if (!META_INTEGRATION_ENABLED) {
        setMeetingTimeLoading(false)
        return
      }

      setMeetingTimeLoading(true)
      setMeetingTimeError(null)
      try {
        const settings = await meetingAutomationService.getSettings()
        const nextTimes = mapSettingsToMeetingTimes(settings)
        setMeetingTimes(nextTimes)
        setMeetingTimesDraft(nextTimes)
      } catch (error) {
        const message = getMeetingTimeErrorMessage(error, 'actions.meeting_templates.errors.fetch_error')
        setMeetingTimeError(message)
      } finally {
        setMeetingTimeLoading(false)
      }
    }

    loadMeetingSettings()
  }, [isMeetingTemplatesOpen])

  useEffect(() => {
    if (!isBetterplaceOpen) return
    const loadBetterplace = async () => {
      if (!META_INTEGRATION_ENABLED) {
        setBetterplaceLoading(false)
        setCrossCriteriaLoading(false)
        setCrossSettingsLoading(false)
        return
      }

      setBetterplaceLoading(true)
      setCrossCriteriaLoading(true)
      setCrossSettingsLoading(true)
      setWhatsappDispatchLoading(true)
      setBetterplaceError(null)
      setCrossCriteriaError(null)
      setCrossSettingsError(null)
      try {
        const [syncConfig, criteria, settings, dispatchState] = await Promise.all([
          betterplaceService.getSyncConfig().catch(() => ({
            enabled: false,
            interval_minutes: 24 * 60,
            max_listings: BETTERPLACE_DEFAULT_MAX_LISTINGS,
          })),
          betterplaceService.getCrossCriteria().catch(() => ({})),
          betterplaceService.getCrossSettings().catch(() => ({})),
          betterplaceService.getTemplateDispatch().catch(() => ({ enabled: false })),
        ])
        setWhatsappDispatchEnabled(Boolean(dispatchState.enabled))
        const interval_hours = (syncConfig.interval_minutes ?? 24 * 60) / 60
        setBetterplaceDraft({
          enabled: syncConfig.enabled ?? false,
          interval_hours: interval_hours || 24,
          max_listings: syncConfig.max_listings ?? BETTERPLACE_DEFAULT_MAX_LISTINGS
        })
        setInitialBetterplace({
          enabled: syncConfig.enabled ?? false,
          interval_hours: interval_hours || 24,
          max_listings: syncConfig.max_listings ?? BETTERPLACE_DEFAULT_MAX_LISTINGS
        })
        setCrossCriteriaDraft(criteria ?? {})
        setInitialCrossCriteria(criteria ?? {})
        setCrossSettingsDraft(settings ?? {})
        setInitialCrossSettings(settings ?? {})
      } catch (e) {
        setBetterplaceError(e instanceof Error ? e.message : 'Failed to load Betterplace config')
      } finally {
        setBetterplaceLoading(false)
        setWhatsappDispatchLoading(false)
        setCrossCriteriaLoading(false)
        setCrossSettingsLoading(false)
      }
    }
    loadBetterplace()
  }, [isBetterplaceOpen])

  const loadCalendarBindings = async () => {
    setCalendarBindingsLoading(true)
    setCalendarBindingsError(null)
    try {
      const [bindings, config, closures] = await Promise.all([
        visitSchedulingService.listBindings(),
        visitSchedulingService.getConfig(),
        visitSchedulingService.listClosureDays(),
      ])
      setCalendarBindings(
        bindings.map((binding) => ({
          ...binding,
          timezone: binding.timezone || selectedTimezone,
        }))
      )
      // Se fusiona con los defaults: si el backend no devuelve los campos de
      // disponibilidad, los inputs no se quedan en undefined (React los volvería
      // no controlados y avisaría por consola).
      setDurationConfig((prev) => ({ ...prev, ...config }))
      setClosureDays(closures)
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.fetch_error')
      setCalendarBindingsError(message)
    } finally {
      setCalendarBindingsLoading(false)
    }
  }

  useEffect(() => {
    if (!VISIT_SCHEDULING_ENABLED) return
    if (!isMeetingTimeOpen) return
    loadCalendarBindings()
  }, [isMeetingTimeOpen])

  const saveBinding = async (binding: AgentCalendarBinding) => {
    setCalendarBindingsSavingId(binding.id)
    setCalendarBindingsError(null)
    try {
      await visitSchedulingService.updateBinding(binding.id, {
        idGestor: binding.idGestor,
        agentDisplayName: binding.agentDisplayName,
        googleCalendarId: binding.googleCalendarId,
        personalCalendarIds: binding.personalCalendarIds,
        timezone: binding.timezone || selectedTimezone,
      })
      toast.success(t('common.save'))
      await loadCalendarBindings()
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.save_error')
      setCalendarBindingsError(message)
      toast.error(message)
    } finally {
      setCalendarBindingsSavingId(null)
    }
  }

  const createBinding = async () => {
    setCreatingBinding(true)
    setCalendarBindingsError(null)
    try {
      await visitSchedulingService.createBinding({
        ...newBindingDraft,
        timezone: selectedTimezone,
      })
      toast.success(t('common.save'))
      setNewBindingDraft({
        idGestor: '',
        agentDisplayName: '',
        googleCalendarId: '',
        personalCalendarIds: [],
        timezone: selectedTimezone,
      })
      await loadCalendarBindings()
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.create_error')
      setCalendarBindingsError(message)
      toast.error(message)
    } finally {
      setCreatingBinding(false)
    }
  }

  const deleteBinding = async (bindingId: string) => {
    setCalendarBindingsSavingId(bindingId)
    setCalendarBindingsError(null)
    try {
      await visitSchedulingService.deleteBinding(bindingId)
      toast.success(t('actions.binding.deleted'))
      await loadCalendarBindings()
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.delete_error')
      setCalendarBindingsError(message)
      toast.error(message)
    } finally {
      setCalendarBindingsSavingId(null)
    }
  }

  const saveDurationConfig = async () => {
    setDurationSaving(true)
    setCalendarBindingsError(null)
    try {
      const next = await visitSchedulingService.updateConfig(durationConfig)
      setDurationConfig(next)
      toast.success(t('common.save'))
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.duration_save_error')
      setCalendarBindingsError(message)
      toast.error(message)
    } finally {
      setDurationSaving(false)
    }
  }

  const addClosureDay = async () => {
    if (!newClosureDraft.date) return
    setClosureSaving(true)
    setCalendarBindingsError(null)
    try {
      await visitSchedulingService.addClosureDay(newClosureDraft.date, newClosureDraft.reason)
      setClosureDays(await visitSchedulingService.listClosureDays())
      setNewClosureDraft({ date: '', reason: '' })
      toast.success(t('common.save'))
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.duration_save_error')
      setCalendarBindingsError(message)
      toast.error(message)
    } finally {
      setClosureSaving(false)
    }
  }

  const removeClosureDay = async (date: string) => {
    setClosureSaving(true)
    setCalendarBindingsError(null)
    try {
      await visitSchedulingService.deleteClosureDay(date)
      setClosureDays((prev) => prev.filter((day) => day.date !== date))
    } catch (error) {
      const message = getMeetingTimeErrorMessage(error, 'actions.visit.errors.duration_save_error')
      setCalendarBindingsError(message)
      toast.error(message)
    } finally {
      setClosureSaving(false)
    }
  }

  const onToggleWhatsappDispatch = async (enabled: boolean) => {
    setWhatsappDispatchSaving(true)
    setWhatsappDispatchError(null)
    const previous = whatsappDispatchEnabled
    setWhatsappDispatchEnabled(enabled)
    try {
      const res = await betterplaceService.updateTemplateDispatch(enabled)
      setWhatsappDispatchEnabled(Boolean(res.enabled))
    } catch (e) {
      setWhatsappDispatchEnabled(previous)
      setWhatsappDispatchError(
        e instanceof Error ? e.message : 'No se pudo cambiar el estado de los envíos de WhatsApp',
      )
    } finally {
      setWhatsappDispatchSaving(false)
    }
  }

  const onSaveBetterplace = async () => {
    setBetterplaceSaving(true)
    setBetterplaceError(null)
    try {
      const res = await betterplaceService.updateSyncConfig({
        enabled: betterplaceDraft.enabled,
        interval_minutes: Math.round(betterplaceDraft.interval_hours * 60),
        max_listings: betterplaceDraft.max_listings
      })
      setInitialBetterplace({
        enabled: res.enabled,
        interval_hours: (res.interval_minutes ?? 0) / 60,
        max_listings: res.max_listings ?? BETTERPLACE_DEFAULT_MAX_LISTINGS
      })
      toast.success(t('common.save'))
    } catch (e) {
      setBetterplaceError(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setBetterplaceSaving(false)
    }
  }

  const onCancelBetterplace = () => {
    if (initialBetterplace) {
      setBetterplaceDraft({
        enabled: initialBetterplace.enabled,
        interval_hours: initialBetterplace.interval_hours,
        max_listings: initialBetterplace.max_listings
      })
    }
  }

  const onSaveCrossCriteria = async () => {
    setCrossCriteriaSaving(true)
    setCrossCriteriaError(null)
    try {
      const data = await betterplaceService.updateCrossCriteria({
        roomsVariance: crossCriteriaDraft.roomsVariance,
        bathroomsVariance: crossCriteriaDraft.bathroomsVariance,
        priceVarianceDown: crossCriteriaDraft.priceVarianceDown,
        priceVarianceUp: crossCriteriaDraft.priceVarianceUp,
        distanceKm: crossCriteriaDraft.distanceKm
      })
      setInitialCrossCriteria(data ?? {})
      setCrossCriteriaDraft(data ?? {})
      toast.success(t('common.save'))
    } catch (e) {
      setCrossCriteriaError(e instanceof Error ? e.message : 'Failed to save criteria')
    } finally {
      setCrossCriteriaSaving(false)
    }
  }

  const onCancelCrossCriteria = () => {
    if (initialCrossCriteria) setCrossCriteriaDraft(initialCrossCriteria)
  }

  const onSaveCrossSettings = async () => {
    setCrossSettingsSaving(true)
    setCrossSettingsError(null)
    try {
      const normalized = buildNormalizedBetterplaceCrossSettings(crossSettingsDraft, betterplaceWhatsappTemplates)
      if (normalized.errors.length > 0) {
        const message = normalized.errors[0]
        setCrossSettingsError(message)
        toast.error(message)
        return
      }

      const data = await betterplaceService.updateCrossSettings(normalized.settings)
      setInitialCrossSettings(data ?? {})
      setCrossSettingsDraft(data ?? {})
      toast.success(t('common.save'))
    } catch (e) {
      setCrossSettingsError(e instanceof Error ? e.message : 'Failed to save settings')
    } finally {
      setCrossSettingsSaving(false)
    }
  }

  const onCancelCrossSettings = () => {
    if (initialCrossSettings) setCrossSettingsDraft(initialCrossSettings)
  }

  const loadAutomations = async () => {
    try {
      setLoading(true)
      const [automationsData, statsData] = await Promise.all([
        automationService.getAllAutomations(),
        automationService.getAutomationStats()
      ])
      setAutomations(automationsData)
      setStats(statsData)
    } catch (error) {
      console.error('Error loading automations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAutomation = () => {
    const lang = window.location.pathname.split('/')[1] || 'en'
    window.location.href = `/${lang}/actions/new/canvas`
  }

  const handleEditAutomation = (automation: Automation) => {
    // Navigate to canvas instead of opening modal
    const lang = window.location.pathname.split('/')[1] || 'en'
    window.location.href = `/${lang}/actions/${automation.id}/canvas`
  }

  // const handleSaveAutomation = async () => {}

  const handleDeleteAutomation = (id: string) => {
    const automation = automations.find(a => a.id === id)
    if (automation) {
      setDeleteDialog({ open: true, automation })
    }
  }

  const confirmDeleteAutomation = async () => {
    if (!deleteDialog.automation) return

    try {
      setDeleteLoading(true)
      await automationService.deleteAutomation(deleteDialog.automation.id)
      setDeleteDialog({ open: false, automation: null })
      await loadAutomations()
    } catch (error) {
      console.error('Error deleting automation:', error)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleToggleStatus = async (id: string) => {
    try {
      await automationService.toggleAutomationStatus(id)
      await loadAutomations()
    } catch (error) {
      console.error('Error toggling automation status:', error)
    }
  }

  const handleUpdateTrigger = async (id: string, newTrigger: Automation['trigger']) => {
    try {
      const automation = automations.find(a => a.id === id)
      if (!automation) return

      const updatedAutomation = { ...automation, trigger: newTrigger }
      await automationService.updateAutomation(updatedAutomation)
      await loadAutomations()
      toast.success('Trigger updated successfully')
    } catch (error) {
      console.error('Error updating trigger:', error)
      toast.error('Failed to update trigger')
    }
  }

  const handleUpdateActions = async (id: string, newActions: Automation['actions']) => {
    try {
      const automation = automations.find(a => a.id === id)
      if (!automation) return

      const updatedAutomation = { ...automation, actions: newActions }
      await automationService.updateAutomation(updatedAutomation)
      await loadAutomations()
      toast.success('Actions updated successfully')
    } catch (error) {
      console.error('Error updating actions:', error)
      toast.error('Failed to update actions')
    }
  }

  // const handleCancelForm = () => {}

  // Canvas view replaces modal form; keep list view always.


  return (
    <div className="flex h-full">
      <div className="w-full p-3 md:p-6 space-y-8 overflow-y-auto">
        {/* Una sola cabecera. Habia tres titulos apilados -dos de ellos `h1`, uno
            escrito a mano en ingles y anunciando unas «Schedules» que no existen-
            y un boton que no podia salir nunca, porque `activeTab` no puede valer
            otra cosa que 'flows'. */}
        <PageHeader
          title={
            <span className="flex flex-wrap items-center gap-2">
              {t('actions.automations')}
              <Badge variant="secondary" className="font-normal">
                {t('functions.alpha_badge')}
              </Badge>
            </span>
          }
          description={t('actions.automations_description')}
        />

        {AUTOMATIZACIONES_LISTAS ? (
          <AutomationStats total={stats.total} active={stats.active} paused={stats.paused} />
        ) : null}

        {/* Tab Switcher */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'flows' | 'schedules')} className="space-y-4">
          {/* Flows tab header (hidden, only one tab) */}
          {/* {false && (
            <div className="border-b">
              <TabsList className="h-12 rounded-none bg-transparent p-0">
                <TabsTrigger value="flows" className="px-6 data-[state=active]:border-b-2 data-[state=active]:border-primary">
                  Flows
                </TabsTrigger>
              </TabsList>
            </div>
          )} */}

          <TabsContent value="flows" className="space-y-4">

            {AUTOMATIZACIONES_LISTAS ? (
              <AutomationList
                automations={automations}
                onEdit={handleEditAutomation}
                onDelete={handleDeleteAutomation}
                onToggleStatus={handleToggleStatus}
                onUpdateTrigger={handleUpdateTrigger}
                onUpdateActions={handleUpdateActions}
                onCreate={handleCreateAutomation}
                loading={loading}
              />
            ) : null}

            {/* Aqui vivia un desplegable «Integraciones y plantillas» que
                agrupaba esto y lo apartaba de las automatizaciones. Sin las
                automatizaciones en pantalla, envolvia la pagina entera y no
                agrupaba nada. */}

            {/* El banco de pruebas entra aqui con lo demas. Estaba suelto arriba
                porque un comentario decia que se usaba a diario; resulta que es
                una herramienta de pruebas -resetea telefonos e inyecta leads
                falsos- y no algo que el cliente haga cada dia. Se queda a mano,
                pero en el mismo sitio que el resto. */}
            <TestBenchCard />

            {/* Instructions */}
            {/* <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-primary/10 p-3">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">💡 Flows</h3>
                    <p className="text-muted-foreground leading-relaxed">Crea procesos con lógica y varios pasos en el canvas.</p>
                  </div>
                </div>
              </CardContent>
            </Card> */}

            {/* Campaign Links Section — visible even when Meta is off (shows disabled state inside) */}
            <div className="space-y-4">
              <SeccionPlegable
                numero="01"
                etiqueta={t('actions.campaign_links.badge_label')}
                titulo={t('actions.campaign_links.title')}
                descripcion={t('actions.campaign_links.description')}
                abierta={isCampaignLinksOpen}
                onAlternar={() => setIsCampaignLinksOpen((prev) => !prev)}
                acciones={
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="shrink-0 rounded-full text-muted-foreground"
                      aria-label="Ayuda variables plantillas Facebook leads"
                      onClick={() => setIsCampaignTemplatesHelpOpen(true)}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                    <FacebookLeadsTemplateHelpDialog
                      open={isCampaignTemplatesHelpOpen}
                      onOpenChange={setIsCampaignTemplatesHelpOpen}
                      closeLabel={t('common.close')}
                    />
                  </>
                }
              >
                      <AutomationTemplateMetaAlerts
                        lang={lang}
                        metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                        whatsappConnected={whatsappConnected}
                        whatsappStatusLoading={whatsappStatusLoading}
                      />
                      {META_INTEGRATION_ENABLED ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-foreground">
                                Instant Forms
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {AUTOMATION_TEMPLATE_UI.campaignConfigureDescription}
                              </p>
                            </div>
                          </div>
                          <InstantFormsList />
                        </div>
                      ) : null}
              </SeccionPlegable>
            </div>

            <div className="space-y-4">
              <PortalLeadSettingsCard />
            </div>

            <div className="space-y-4">
              <PostCallTemplatesSettingsCard />
            </div>

            <div className="space-y-4">
              <WebPortalNotificationsCard />
            </div>

            <div className="space-y-4">
              <RisingOffersNotificationsCard />
            </div>

            <div className="space-y-4">
              <BuyerReactivationNotificationsCard />
            </div>

            <div className="space-y-4">
              <AuthOtpNotificationsCard />
            </div>

            {/* Meeting automation templates */}
            <div className="space-y-4">
              <SeccionPlegable
                numero="04"
                etiqueta={t('actions.meeting_templates.chip_label')}
                titulo={t('actions.meeting_templates.title')}
                descripcion={t('actions.meeting_templates.description')}
                abierta={isMeetingTemplatesOpen}
                onAlternar={() => setIsMeetingTemplatesOpen((prev) => !prev)}
                acciones={
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="shrink-0 rounded-full text-muted-foreground"
                      aria-label={t('actions.meeting_templates.help.cta')}
                      onClick={() => setIsMeetingTemplatesHelpOpen(true)}
                    >
                      <Info className="h-4 w-4" />
                    </Button>

                    <TemplateVariablesHelpDialog
                      open={isMeetingTemplatesHelpOpen}
                      onOpenChange={setIsMeetingTemplatesHelpOpen}
                      title={t('actions.meeting_templates.help.title')}
                      description={t('actions.meeting_templates.help.description')}
                      orderNote={t('actions.meeting_templates.help.order_note')}
                      variablesTitle={t('actions.meeting_templates.help.variables_title')}
                      examplesTitle={t('actions.meeting_templates.help.examples_title')}
                      examplePrefix={t('actions.meeting_templates.help.example_prefix')}
                      variables={meetingTemplateHelpVariables}
                      examples={[
                        {
                          title: t('actions.meeting_templates.help.example_pre_label'),
                          text: t('actions.meeting_templates.help.example_pre_text'),
                        },
                        {
                          title: t('actions.meeting_templates.help.example_post_label'),
                          text: t('actions.meeting_templates.help.example_post_text'),
                        },
                      ]}
                      note={t('actions.meeting_templates.help.meta_note')}
                      closeLabel={t('common.close')}
                    />
                  </>
                }
              >
                    <AutomationTemplateMetaAlerts
                      lang={lang}
                      metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                      whatsappConnected={whatsappConnected}
                      whatsappStatusLoading={whatsappStatusLoading}
                    />
                    {!META_INTEGRATION_ENABLED ? null : meetingTimeLoading ? (
                      <div className="px-1 py-3 text-sm text-muted-foreground">
                        {AUTOMATION_TEMPLATE_UI.templatesLoadingMessage}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-foreground">
                            {AUTOMATION_TEMPLATE_UI.configureTitle}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {AUTOMATION_TEMPLATE_UI.meetingConfigureDescription}
                          </p>
                        </div>
                        {whatsappTemplatesLoadError && !whatsappTemplatesLoading && whatsappConnected && (
                          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            {whatsappTemplatesLoadError}
                          </div>
                        )}
                        {showEmptyTemplatesHint && (
                          <div className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                            {t('actions.meeting_templates.templates_empty_hint')}
                          </div>
                        )}
                        {showLegacyFallbackHint && (
                          <div className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                            {t('actions.meeting_templates.legacy_fallback_hint')}
                          </div>
                        )}
                        <div className="overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>{t('actions.meeting_templates.name')}</TableHead>
                                <TableHead>Automation</TableHead>
                                <TableHead>{t('actions.meeting_templates.offset')}</TableHead>
                                <TableHead>{t('actions.meeting_templates.direction')}</TableHead>
                                <TableHead>{t('actions.meeting_templates.linked_template')}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {meetingTimesDraft.map((mt) => (
                                <TableRow key={mt.id}>
                                  <TableCell className="font-medium">
                                    {mt.id === 'pre_visit'
                                      ? t('actions.meeting_templates.slot_pre')
                                      : t('actions.meeting_templates.slot_post')}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={mt.enabled}
                                        disabled={meetingTimeSaving}
                                        onCheckedChange={(checked) => updateMeetingTimeDraft(mt.id, { enabled: checked })}
                                        aria-label={`Toggle ${mt.id} automation`}
                                      />
                                      <Badge variant={mt.enabled ? 'default' : 'secondary'}>
                                        {mt.enabled ? 'Active' : 'Paused'}
                                      </Badge>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Select
                                      value={String(mt.minutes)}
                                      disabled={!mt.enabled || meetingTimeSaving}
                                      onValueChange={(value) =>
                                        updateMeetingTimeDraft(mt.id, { minutes: Number(value) })
                                      }
                                    >
                                      <SelectTrigger className="h-9 w-40">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {!FIXED_MEETING_TIME_OPTIONS.some((option) => option.value === mt.minutes) && (
                                          <SelectItem value={String(mt.minutes)}>{`${mt.minutes} minutes (legacy)`}</SelectItem>
                                        )}
                                        {FIXED_MEETING_TIME_OPTIONS.map((option) => (
                                          <SelectItem key={option.value} value={String(option.value)}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    {mt.direction === 'Before'
                                      ? t('actions.meeting_templates.before')
                                      : t('actions.meeting_templates.after')}
                                  </TableCell>
                                  <TableCell>
                                    {/*
                                      Hide internal legacy names from the selector UI. When there are no approved
                                      Meta templates at all, keep the control visibly locked instead.
                                    */}
                                    <div className="flex items-center gap-2">
                                      <Select
                                        value={
                                          LEGACY_MEETING_TEMPLATE_NAMES.has(mt.linkedTemplate)
                                            ? undefined
                                            : mt.linkedTemplate
                                        }
                                        disabled={!mt.enabled || !hasSelectableMeetingTemplates || meetingTimeSaving}
                                        onValueChange={(value) => {
                                          const template = templateByName.get(value)
                                          const analysis = analyzeMeetingTemplate(template, value)
                                          updateMeetingTimeDraft(mt.id, {
                                            linkedTemplate: value,
                                            templateLanguage: template?.language || mt.templateLanguage,
                                            bodyVariableCount: analysis.bodyVariableCount,
                                            variableMapping: analysis.bodyVariableCount > 0
                                              ? buildDefaultMeetingVariableMapping(analysis.bodyVariableCount)
                                              : [],
                                          })
                                        }}
                                      >
                                        <SelectTrigger className="w-full h-9">
                                          <SelectValue
                                            placeholder={
                                              hasSelectableMeetingTemplates
                                                ? t('actions.meeting_templates.select_placeholder')
                                                : t('actions.meeting_templates.select_locked_empty')
                                            }
                                          />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {meetingTemplateOptions.map((name) => {
                                            const template = templateByName.get(name)
                                            const analysis = analyzeMeetingTemplate(template, name)
                                            return (
                                              <SelectItem key={name} value={name} disabled={!analysis.compatible}>
                                                {analysis.compatible ? name : `${name} (incompatible)`}
                                              </SelectItem>
                                            )
                                          })}
                                        </SelectContent>
                                      </Select>
                                      {mt.bodyVariableCount > 0 && !LEGACY_MEETING_TEMPLATE_NAMES.has(mt.linkedTemplate) && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="shrink-0"
                                          disabled={!mt.enabled || meetingTimeSaving}
                                          onClick={() => {
                                            const mapping = mt.variableMapping.length === mt.bodyVariableCount
                                              ? mt.variableMapping
                                              : buildDefaultMeetingVariableMapping(mt.bodyVariableCount)
                                            setVarMappingModal({ open: true, slotId: mt.id, draft: [...mapping] })
                                          }}
                                        >
                                          <Settings className="h-4 w-4 mr-1" />
                                          Variables
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    {(whatsappTemplatesLoading || whatsappStatusLoading) && (
                      <div className="mt-3 text-sm text-muted-foreground">
                        {t('actions.meeting_templates.templates_loading')}
                      </div>
                    )}
                    {meetingTimeError && (
                      <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {meetingTimeError}
                      </div>
                    )}
                    {hasMeetingTimeChanges && (
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={handleCancelMeetingTimes}>
                          {t('common.cancel')}
                        </Button>
                        <Button
                          size="sm"
                          disabled={meetingTimeSaving || hasIncompatibleMeetingTemplates}
                          onClick={handleSaveMeetingTimes}
                        >
                          {t('common.save')}
                        </Button>
                      </div>
                    )}
              </SeccionPlegable>
            </div>

            {/* Visit calendar bindings and durations */}
            <div className="space-y-4">
              <SeccionPlegable
                numero="05"
                etiqueta={t('actions.visit.chip_label')}
                titulo={t('actions.visit.title')}
                descripcion={t('actions.visit.description')}
                abierta={isMeetingTimeOpen}
                onAlternar={() => setIsMeetingTimeOpen((prev) => !prev)}
              >
                  <div className="space-y-4">
                    <section className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          {t('actions.visit.duration_title')}
                        </h4>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                          <label className="grid gap-1">
                            <span className="text-sm text-muted-foreground">{t('actions.visit.different_property_minutes')}</span>
                            <Input
                              type="number"
                              min={1}
                              
                              value={durationConfig.differentPropertyMinutes}
                              onChange={(e) =>
                                setDurationConfig((prev) => ({
                                  ...prev,
                                  differentPropertyMinutes: Number(e.target.value || 0),
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-1">
                            <span className="text-sm text-muted-foreground">{t('actions.visit.same_property_minutes')}</span>
                            <Input
                              type="number"
                              min={1}
                              
                              value={durationConfig.samePropertyMinutes}
                              onChange={(e) =>
                                setDurationConfig((prev) => ({
                                  ...prev,
                                  samePropertyMinutes: Number(e.target.value || 0),
                                }))
                              }
                            />
                          </label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('actions.visit.duration_hint')}
                        </p>

                        <div className="border-t border-border pt-3 space-y-2">
                          <h4 className="text-sm font-medium text-muted-foreground">
                            {t('actions.visit.availability_title')}
                          </h4>

                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            <label className="grid gap-1">
                              <span className="text-sm text-muted-foreground">
                                {t('actions.visit.min_notice_hours')}
                              </span>
                              <Input
                                type="number"
                                min={0}
                                
                                value={durationConfig.minNoticeHours ?? 0}
                                onChange={(e) =>
                                  setDurationConfig((prev) => ({
                                    ...prev,
                                    minNoticeHours: Number(e.target.value || 0),
                                  }))
                                }
                              />
                            </label>
                            <label className="flex items-center gap-2 md:mt-6">
                              <Switch
                                checked={durationConfig.minNoticeBusinessHours ?? true}
                                onCheckedChange={(checked) =>
                                  setDurationConfig((prev) => ({
                                    ...prev,
                                    minNoticeBusinessHours: checked,
                                  }))
                                }
                              />
                              <span className="text-sm text-muted-foreground">
                                {t('actions.visit.min_notice_business_hours')}
                              </span>
                            </label>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t('actions.visit.min_notice_hint')}
                          </p>

                          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                            {([
                              ['workdayMorningStart', 'actions.visit.morning_start'],
                              ['workdayMorningEnd', 'actions.visit.morning_end'],
                              ['workdayAfternoonStart', 'actions.visit.afternoon_start'],
                              ['workdayAfternoonEnd', 'actions.visit.afternoon_end'],
                            ] as const).map(([field, labelKey]) => (
                              <label key={field} className="grid gap-1">
                                <span className="text-sm text-muted-foreground">{t(labelKey)}</span>
                                <Input
                                  type="time"
                                  
                                  value={durationConfig[field] ?? ''}
                                  onChange={(e) =>
                                    setDurationConfig((prev) => ({
                                      ...prev,
                                      [field]: e.target.value,
                                    }))
                                  }
                                />
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t('actions.visit.afternoon_hint')}
                          </p>

                          <div className="grid gap-1">
                            <span className="text-sm text-muted-foreground">
                              {t('actions.visit.workdays')}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {WEEKDAY_LABEL_KEYS.map((labelKey, index) => {
                                const active = (durationConfig.workdays ?? []).includes(index)
                                return (
                                  <Button
                                    key={labelKey}
                                    type="button"
                                    size="sm"
                                    variant={active ? 'default' : 'outline'}
                                    onClick={() =>
                                      setDurationConfig((prev) => {
                                        const current = prev.workdays ?? []
                                        return {
                                          ...prev,
                                          workdays: active
                                            ? current.filter((day) => day !== index)
                                            : [...current, index].sort((a, b) => a - b),
                                        }
                                      })
                                    }
                                  >
                                    {t(labelKey)}
                                  </Button>
                                )
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button size="sm" onClick={saveDurationConfig} disabled={durationSaving}>
                            {t('common.save')}
                          </Button>
                        </div>
                    </section>

                    <section className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          {t('actions.visit.closure_title')}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {t('actions.visit.closure_hint')}
                        </p>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-[auto_1fr_auto]">
                          <Input
                            type="date"
                            
                            value={newClosureDraft.date}
                            onChange={(e) =>
                              setNewClosureDraft((prev) => ({ ...prev, date: e.target.value }))
                            }
                          />
                          <Input
                            
                            placeholder={t('actions.visit.closure_reason')}
                            value={newClosureDraft.reason}
                            onChange={(e) =>
                              setNewClosureDraft((prev) => ({ ...prev, reason: e.target.value }))
                            }
                          />
                          <Button
                            size="sm"
                            onClick={addClosureDay}
                            disabled={closureSaving || !newClosureDraft.date}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {t('actions.visit.closure_add')}
                          </Button>
                        </div>
                        {closureDays.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {t('actions.visit.closure_empty')}
                          </p>
                        ) : (
                          <ul className="divide-y divide-border">
                            {closureDays.map((day) => (
                              <li
                                key={day.date}
                                className="flex items-center justify-between gap-2 px-3 py-2"
                              >
                                <span className="text-sm">
                                  {day.date}
                                  {day.reason ? (
                                    <span className="text-muted-foreground"> · {day.reason}</span>
                                  ) : null}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeClosureDay(day.date)}
                                  disabled={closureSaving}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                    </section>

                    <section className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">{t('actions.visit.create_title')}</h4>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                          <Input
                            
                            placeholder={t('actions.visit.agent_name')}
                            value={newBindingDraft.agentDisplayName}
                            onChange={(e) => setNewBindingDraft((prev) => ({ ...prev, agentDisplayName: e.target.value }))}
                          />
                          <Textarea
                            className="min-h-20"
                            placeholder={t('actions.visit.personal_calendar_ids')}
                            value={(newBindingDraft.personalCalendarIds ?? []).join('\n')}
                            onChange={(e) => setNewBindingDraft((prev) => ({
                              ...prev,
                              personalCalendarIds: e.target.value.split('\n').map((id) => id.trim()).filter(Boolean),
                            }))}
                          />
                          <Input
                            
                            placeholder={t('actions.visit.calendar_id')}
                            value={newBindingDraft.googleCalendarId}
                            onChange={(e) => setNewBindingDraft((prev) => ({ ...prev, googleCalendarId: e.target.value }))}
                          />
                          <Input
                            
                            placeholder={t('actions.visit.agent_id')}
                            value={newBindingDraft.idGestor}
                            onChange={(e) => setNewBindingDraft((prev) => ({ ...prev, idGestor: e.target.value }))}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button onClick={createBinding} disabled={creatingBinding}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('actions.visit.create_button')}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() =>
                              setNewBindingDraft({
                                idGestor: '',
                                agentDisplayName: '',
                                googleCalendarId: '',
                                personalCalendarIds: [],
                                timezone: selectedTimezone,
                              })
                            }
                          >
                            {t('common.clear')}
                          </Button>
                        </div>
                    </section>

                    <section>
                        <SeccionPlegable
                          plana
                          titulo={`${t('actions.visit.table_title')} (${calendarBindings.length})`}
                          descripcion={t('actions.visit.table_subtitle')}
                          abierta={isAssignmentsOpen}
                          onAlternar={() => setIsAssignmentsOpen((prev) => !prev)}
                        >
                          <div className="space-y-2">
                            {calendarBindingsError && (
                              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                {calendarBindingsError}
                              </div>
                            )}
                            {calendarBindingsLoading ? (
                              <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                            ) : (
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>{t('actions.visit.agent_name')}</TableHead>
                                      <TableHead>{t('actions.visit.calendar_id')}</TableHead>
                                      <TableHead>{t('actions.visit.personal_calendar_ids')}</TableHead>
                                      <TableHead>{t('actions.visit.agent_id')}</TableHead>
                                      <TableHead>{t('actions.visit.actions')}</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {calendarBindings.map((binding) => (
                                      <TableRow key={binding.id}>
                                        <TableCell>
                                          <Input
                                            className="w-44"
                                            value={binding.agentDisplayName}
                                            onChange={(e) => updateBindingDraft(binding.id, { agentDisplayName: e.target.value })}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Input
                                            className="w-60"
                                            value={binding.googleCalendarId}
                                            onChange={(e) => updateBindingDraft(binding.id, { googleCalendarId: e.target.value })}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Textarea
                                            className="min-h-16 w-60"
                                            placeholder={t('actions.visit.personal_calendar_ids')}
                                            value={(binding.personalCalendarIds ?? []).join('\n')}
                                            onChange={(e) => updateBindingDraft(binding.id, {
                                              personalCalendarIds: e.target.value.split('\n').map((id) => id.trim()).filter(Boolean),
                                            })}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Input
                                            className="w-28"
                                            value={binding.idGestor}
                                            onChange={(e) => updateBindingDraft(binding.id, { idGestor: e.target.value })}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <div className="flex items-center gap-2">
                                            <Button
                                              size="sm"
                                              onClick={() => saveBinding(binding)}
                                              disabled={calendarBindingsSavingId === binding.id}
                                            >
                                              {t('common.save')}
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => deleteBinding(binding.id)}
                                              disabled={calendarBindingsSavingId === binding.id}
                                            >
                                              {t('common.delete')}
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {calendarBindings.length === 0 && (
                                      <TableRow>
                                        <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                                          {t('actions.visit.empty_state')}
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </div>
                        </SeccionPlegable>
                    </section>

                  </div>
              </SeccionPlegable>
            </div>

            {/* 04 Betterplace – sync, cross criteria, WhatsApp templates */}
            <div className="space-y-4">
              <SeccionPlegable
                numero="06"
                etiqueta={t('actions.betterplace.chip_label')}
                titulo={t('actions.betterplace.title')}
                descripcion={t('actions.betterplace.description')}
                abierta={isBetterplaceOpen}
                onAlternar={() => setIsBetterplaceOpen((prev) => !prev)}
                acciones={
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="shrink-0 rounded-full text-muted-foreground"
                      aria-label="How Betterplace template variables are filled"
                      onClick={() => setIsBetterplaceTemplatesHelpOpen(true)}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                    <TemplateVariablesHelpDialog
                      open={isBetterplaceTemplatesHelpOpen}
                      onOpenChange={setIsBetterplaceTemplatesHelpOpen}
                      title="Betterplace template variables"
                      description="Buyer outreach and RK Iglesias team follow-up templates can use these backend fields."
                      orderNote="The exact {{1}}, {{2}}, ... order is configured by template_buyers_variables and template_coordination_variables. Defaults follow this list."
                      variables={BETTERPLACE_TEMPLATE_HELP_VARIABLES}
                      examples={[
                        {
                          title: 'Plantilla contacto',
                          text: 'Hola {{1}}, tenemos una vivienda que puede encajar contigo: {{3}}. Puedes verla aqui: {{4}}.',
                        },
                        {
                          title: 'Plantilla empleados',
                          text: 'Lead interesado: {{1}} {{2}}. Propiedad: {{3}}. Cruce: {{7}}.',
                        },
                      ]}
                      note="Backend source: BetterplacePropertyTemplateApplicationService for buyer sends and BetterplaceCoordinationFollowupService for RK Iglesias team follow-up."
                      closeLabel={t('common.close')}
                    />
                  </>
                }
              >
                      <AutomationTemplateMetaAlerts
                        lang={lang}
                        metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                        whatsappConnected={whatsappConnected}
                        whatsappStatusLoading={whatsappStatusLoading}
                      />
                      {META_INTEGRATION_ENABLED ? (
                        <BetterplaceCrucesForm
                          betterplaceDraft={betterplaceDraft}
                          setBetterplaceDraft={setBetterplaceDraft}
                          betterplaceLoading={betterplaceLoading}
                          betterplaceSaving={betterplaceSaving}
                          betterplaceError={betterplaceError}
                          hasBetterplaceChanges={hasBetterplaceChanges}
                          onSaveBetterplace={onSaveBetterplace}
                          onCancelBetterplace={onCancelBetterplace}
                          crossCriteriaDraft={crossCriteriaDraft}
                          setCrossCriteriaDraft={setCrossCriteriaDraft}
                          crossCriteriaLoading={crossCriteriaLoading}
                          crossCriteriaSaving={crossCriteriaSaving}
                          crossCriteriaError={crossCriteriaError}
                          hasCrossCriteriaChanges={hasCrossCriteriaChanges}
                          onSaveCrossCriteria={onSaveCrossCriteria}
                          onCancelCrossCriteria={onCancelCrossCriteria}
                          crossSettingsDraft={crossSettingsDraft}
                          setCrossSettingsDraft={setCrossSettingsDraft}
                          crossSettingsLoading={crossSettingsLoading}
                          crossSettingsSaving={crossSettingsSaving}
                          crossSettingsError={crossSettingsError}
                          hasCrossSettingsChanges={hasCrossSettingsChanges}
                          onSaveCrossSettings={onSaveCrossSettings}
                          onCancelCrossSettings={onCancelCrossSettings}
                          connectedWabaId={whatsappStatus?.account_id}
                          whatsappConnected={whatsappConnected}
                          onWhatsappTemplatesChange={setBetterplaceWhatsappTemplates}
                          lang={lang}
                          whatsappDispatchEnabled={whatsappDispatchEnabled}
                          whatsappDispatchLoading={whatsappDispatchLoading}
                          whatsappDispatchSaving={whatsappDispatchSaving}
                          whatsappDispatchError={whatsappDispatchError}
                          onToggleWhatsappDispatch={(enabled) => void onToggleWhatsappDispatch(enabled)}
                        />
                      ) : null}
              </SeccionPlegable>
            </div>

            {/*
              07 Moviles de comerciales. Va detras de la 06 para que la lista de
              automatizaciones se lea en orden: antes estaba arriba del todo,
              fuera de su numero, y rompia la secuencia.
            */}
            <div className="space-y-4">
              <ComercialPhoneBindingsCard />
              <ExcludedAgentsCard />
              <AgentNoticeSettingsCard />
              <ConsentModesCard />
            </div>


            {/* Variable mapping modal for pre/post visit templates */}
            <Dialog
              open={varMappingModal.open}
              onOpenChange={(open) => setVarMappingModal((prev) => ({ ...prev, open }))}
            >
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {varMappingModal.slotId === 'pre_visit' ? 'Variables — Previsita' : 'Variables — Postvisita'}
                  </DialogTitle>
                </DialogHeader>
                {varMappingModal.slotId && (() => {
                  const mt = meetingTimesDraft.find((m) => m.id === varMappingModal.slotId)
                  const template = mt ? templateByName.get(mt.linkedTemplate) : undefined
                  const bodyText = template?.components?.find((c) => c.type?.toUpperCase() === 'BODY')?.text ?? ''
                  return (
                    <div className="space-y-4">
                      {bodyText && (
                        <div className="rounded-md bg-muted/40 px-3 py-2 text-sm font-mono whitespace-pre-wrap">
                          {bodyText}
                        </div>
                      )}
                      <div className="space-y-2">
                        {varMappingModal.draft.map((entry, idx) => (
                          <div key={entry.position} className="flex items-center gap-2">
                            <span className="w-10 shrink-0 text-sm font-medium text-muted-foreground">{`{{${entry.position}}}`}</span>
                            <Select
                              value={entry.source_key}
                              onValueChange={(key) => {
                                const updated = [...varMappingModal.draft]
                                updated[idx] = { ...entry, source_key: key as MeetingVariableMappingEntry['source_key'] }
                                setVarMappingModal((prev) => ({ ...prev, draft: updated }))
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => (
                                  <SelectItem key={spec.key} value={spec.key}>
                                    {spec.label}
                                    <span className="ml-2 text-xs text-muted-foreground">({spec.example})</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setVarMappingModal((prev) => ({ ...prev, open: false }))}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => {
                      if (varMappingModal.slotId) {
                        updateMeetingTimeDraft(varMappingModal.slotId, { variableMapping: varMappingModal.draft })
                      }
                      setVarMappingModal((prev) => ({ ...prev, open: false }))
                    }}
                  >
                    Guardar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Schedules, Credentials, and Executions tabs are commented out - not currently used */}
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={deleteDialog.open}
          onOpenChange={(open) => setDeleteDialog({ open, automation: deleteDialog.automation })}
          automationName={deleteDialog.automation?.name || ''}
          onConfirm={confirmDeleteAutomation}
          loading={deleteLoading}
        />
      </div>
    </div>
  )
}



// Note: UI-only, no backend wiring yet
// Keeping extra components at end of file for clarity
